import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, inject, Injectable } from '@angular/core';
import { Environment } from '../../environment/environment.model';
import { catchError, Observable, shareReplay, throwError } from 'rxjs';
import { WorkProcessWithClient } from '../_models/social-domain/workprocess-with-client.model';
import { Client } from '../_models/social-domain/client.model';
import { WorkProcess } from '../_models/social-domain/workprocess.model';

@Injectable({
  providedIn: 'root',
})
export class SocialDomainService {
  constructor(@Inject('env') private environment: Environment) { }
  private httpClient = inject(HttpClient);

  private SOCIAL_DOMAIN_PATH = 'suites';

  /** Gedeelde cliëntenlijst; zie getAllClients(). */
  private allClients$?: Observable<Client[]>;

  public getWorkProcesses(): Observable<WorkProcessWithClient[]> {
    return this.httpClient.get<WorkProcessWithClient[]>(
      `${this.environment.apiUrl}/${this.SOCIAL_DOMAIN_PATH}/werkprocesses-with-clients`,
    );
  }

  /**
   * Haalt álle cliënten op voor de cliëntkiezer, die lokaal filtert. Het gewone
   * cliënten-endpoint levert maar één OData-pagina; dit endpoint pagineert door.
   * De resultaten bevatten bewust geen BSN.
   *
   * De lijst telt al snel duizenden cliënten en wordt in elke workflowstap opnieuw
   * gevraagd, dus het antwoord wordt voor de duur van de sessie gedeeld: alleen de
   * eerste stap wacht op de SSD, de rest krijgt hem direct. Een mislukte call wordt
   * niet vastgehouden, zodat een volgende stap het opnieuw kan proberen.
   */
  public getAllClients(): Observable<Client[]> {
    this.allClients$ ??= this.httpClient
      .get<Client[]>(`${this.environment.apiUrl}/${this.SOCIAL_DOMAIN_PATH}/clients/all`)
      .pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError((error) => {
          this.allClients$ = undefined;
          return throwError(() => error);
        }),
      );

    return this.allClients$;
  }

  public getWorkProcessesByClient(clientNummer: number): Observable<WorkProcess[]> {
    return this.httpClient.get<WorkProcess[]>(
      `${this.environment.apiUrl}/${this.SOCIAL_DOMAIN_PATH}/werkprocesses/client/${clientNummer}`,
    );
  }

  public getDocumentDetails(
    clientNummer: number,
    werkprocesNummer?: number,
    top?: number,
    skip?: number,
    filter?: string,
    select?: string,
    orderby?: string,
    expand?: string,
    count?: boolean,
  ): Observable<unknown> {
    let params = new HttpParams().set('clientNummer', clientNummer);

    if (werkprocesNummer != null) params = params.set('werkprocesNummer', werkprocesNummer);
    if (top != null) params = params.set('$top', top);
    if (skip != null) params = params.set('$skip', skip);
    if (filter != null) params = params.set('$filter', filter);
    if (select != null) params = params.set('$select', select);
    if (orderby != null) params = params.set('$orderby', orderby);
    if (expand != null) params = params.set('$expand', expand);
    if (count != null) params = params.set('$count', count);

    return this.httpClient.get<unknown>(
      `${this.environment.apiUrl}/${this.SOCIAL_DOMAIN_PATH}/document-details`,
      { params },
    );
  }

  public uploadDocument(
    werkprocesNummer: number,
    file: Blob,
    fileName: string,
  ): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, fileName);
    return this.httpClient.post(
      `${this.environment.apiUrl}/${this.SOCIAL_DOMAIN_PATH}/upload-document/${werkprocesNummer}`,
      formData,
      { responseType: 'text' },
    );
  }
}
